import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../utils/formatPrice';
import { calculateOrderTotals } from '../../utils/taxCalc';
import { toast } from '../../lib/toast';
import PriceTag from '../ui/PriceTag';
import './SettingsTab.css';

export default function SettingsTab({
  restaurantName,
  onRestaurantNameChange,
  restaurantSlug = '',
  onRestaurantSlugChange,
  isAdmin,
  currencies,
  restaurantCurrency,
  onCurrencyChange,
  taxConfig,
  onTaxConfigChange,
  requireCustomerOtp = false,
  onRequireCustomerOtpChange,
  saving,
  onSubmit,
}) {
  const { t } = useTranslation();
  const tax = useMemo(
    () => taxConfig || { tax_enabled: false, tax_rate: 0, tax_inclusive: true, tax_label: 'VAT', tax_number: '' },
    [taxConfig]
  );
  const updateTax = (patch) => onTaxConfigChange && onTaxConfigChange({ ...tax, ...patch });

  const previewBase = 100;
  const preview = useMemo(
    () => calculateOrderTotals([{ price: previewBase, quantity: 1 }], tax),
    [tax]
  );

  const taxLabel = (tax.tax_label || 'VAT').trim() || 'VAT';

  const menuLink = restaurantSlug
    ? `${window.location.origin}/menu/${restaurantSlug}`
    : '';

  const copyMenuLink = async () => {
    if (!menuLink) return;
    try {
      await navigator.clipboard.writeText(menuLink);
      toast.success(t('admin.slug.copied'));
    } catch (_) {
      /* clipboard unavailable — no-op */
    }
  };

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h3>⚙️ {t('admin.settings.title')}</h3>
      </div>
      <div className="admin-card-body">
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label>{t('admin.restaurantName')}</label>
            <input
              type="text"
              className="form-input"
              value={restaurantName}
              onChange={(e) => onRestaurantNameChange(e.target.value)}
              placeholder={t('admin.addMenuItem.itemNamePlaceholder')}
              disabled={!isAdmin}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.slug.label')}</label>
            <input
              type="text"
              className="form-input"
              value={restaurantSlug}
              onChange={(e) => onRestaurantSlugChange && onRestaurantSlugChange(e.target.value)}
              placeholder={t('admin.slug.placeholder')}
              disabled={!isAdmin}
              style={{ maxWidth: '320px' }}
            />
            <small style={{ color: '#9ca3af', marginTop: '0.375rem', display: 'block' }}>
              {isAdmin ? t('admin.slug.help') : t('admin.slug.adminOnly')}
            </small>
            {menuLink && (
              <div
                style={{
                  marginTop: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                  {t('admin.slug.menuLinkLabel')}:
                </span>
                <a href={menuLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem' }}>
                  {menuLink}
                </a>
                <button type="button" className="btn btn-secondary btn-sm" onClick={copyMenuLink}>
                  {t('admin.slug.copy')}
                </button>
              </div>
            )}
          </div>
          <div className="form-group">
            <label>{t('admin.currency.label')}</label>
            <select
              className="form-input"
              value={restaurantCurrency.code}
              onChange={(e) => {
                const c = currencies.find((cu) => cu.code === e.target.value) || { code: 'USD', symbol: '$', position: 'before' };
                onCurrencyChange({ code: c.code, symbol: c.symbol, position: c.position || 'before' });
              }}
              style={{ maxWidth: '320px' }}
            >
              {currencies.length === 0 && <option value="USD">USD — US Dollar ($14.99)</option>}
              {currencies.map((c) => (
                <option key={c.code} value={c.code}>{c.code} — {c.name} ({c.example})</option>
              ))}
            </select>
            <small style={{ color: '#9ca3af', marginTop: '0.375rem', display: 'block' }}>
              {t('admin.currency.preview')}:{' '}
              <PriceTag amount={14.99} currency={restaurantCurrency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
            </small>
          </div>

          <div className="tax-settings">
            <div className="tax-settings-header">
              <h4 className="tax-settings-title">
                <span aria-hidden="true">🧾</span>
                {t('admin.tax.section')}
              </h4>
              <label className="tax-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(tax.tax_enabled)}
                  onChange={(e) => updateTax({ tax_enabled: e.target.checked })}
                />
                <span className="tax-toggle-track" aria-hidden="true" />
                <span className="tax-toggle-label">{t('admin.tax.enabled')}</span>
              </label>
            </div>

            {!tax.tax_enabled ? (
              <div className="tax-disabled-notice" role="status">
                <span aria-hidden="true">💤</span>
                <span>{t('admin.tax.disabledNotice')}</span>
              </div>
            ) : (
              <>
                <div className="tax-fields">
                  <div className="tax-row">
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.tax.rate')}</label>
                      <div className="tax-rate-input-wrap">
                        <input
                          type="number"
                          className="form-input"
                          min="0"
                          max="100"
                          step="0.01"
                          value={tax.tax_rate}
                          onChange={(e) => updateTax({ tax_rate: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>{t('admin.tax.label')}</label>
                      <input
                        type="text"
                        className="form-input"
                        maxLength={50}
                        value={tax.tax_label}
                        onChange={(e) => updateTax({ tax_label: e.target.value })}
                        placeholder="VAT"
                      />
                      <span className="tax-help">{t('admin.tax.labelHelp')}</span>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('admin.tax.mode')}</label>
                    <div
                      className="tax-mode-control"
                      role="radiogroup"
                      aria-label={t('admin.tax.mode')}
                    >
                      <label className={`tax-mode-option ${tax.tax_inclusive ? 'is-active' : ''}`}>
                        <input
                          type="radio"
                          name="tax-mode"
                          checked={Boolean(tax.tax_inclusive)}
                          onChange={() => updateTax({ tax_inclusive: true })}
                        />
                        <span className="tax-mode-option-title">{t('admin.tax.modeInclusive')}</span>
                        <span className="tax-mode-option-hint">{t('admin.tax.modeInclusiveHint')}</span>
                      </label>
                      <label className={`tax-mode-option ${!tax.tax_inclusive ? 'is-active' : ''}`}>
                        <input
                          type="radio"
                          name="tax-mode"
                          checked={!tax.tax_inclusive}
                          onChange={() => updateTax({ tax_inclusive: false })}
                        />
                        <span className="tax-mode-option-title">{t('admin.tax.modeExclusive')}</span>
                        <span className="tax-mode-option-hint">{t('admin.tax.modeExclusiveHint')}</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('admin.tax.number')}</label>
                    <input
                      type="text"
                      className="form-input"
                      maxLength={50}
                      value={tax.tax_number || ''}
                      onChange={(e) => updateTax({ tax_number: e.target.value })}
                      placeholder="—"
                      style={{ maxWidth: '320px' }}
                    />
                    <span className="tax-help">{t('admin.tax.numberHelp')}</span>
                  </div>
                </div>

                {preview.tax_active && preview.tax_amount > 0 && (
                  <div className="tax-preview" aria-live="polite">
                    <div className="tax-preview-header">
                      <span className="tax-preview-title">{t('admin.tax.preview')}</span>
                      <span className="tax-preview-hint">
                        {t('admin.tax.previewHint', { base: formatPrice(previewBase, restaurantCurrency) })}
                      </span>
                    </div>
                    <div className="tax-preview-rows">
                      <div className="tax-preview-row is-muted">
                        <span>{t('tax.subtotal')}</span>
                        <PriceTag amount={preview.subtotal} currency={restaurantCurrency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                      </div>
                      <div className="tax-preview-row is-muted">
                        <span>
                          {taxLabel} ({preview.tax_rate}%)
                          {preview.tax_inclusive ? t('tax.includedSuffix') : ''}
                        </span>
                        <PriceTag amount={preview.tax_amount} currency={restaurantCurrency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                      </div>
                      <div className="tax-preview-row is-total">
                        <span>{t('tax.total')}</span>
                        <PriceTag amount={preview.total} currency={restaurantCurrency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="tax-settings" style={{ marginTop: '1rem' }}>
            <div className="tax-settings-header">
              <h4 className="tax-settings-title">
                <span aria-hidden="true">📱</span>
                {t('admin.customerAuth.section')}
              </h4>
              <label className="tax-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(requireCustomerOtp)}
                  onChange={(e) => onRequireCustomerOtpChange && onRequireCustomerOtpChange(e.target.checked)}
                />
                <span className="tax-toggle-track" aria-hidden="true" />
                <span className="tax-toggle-label">{t('admin.customerAuth.requireOtp')}</span>
              </label>
            </div>
            <div className="tax-disabled-notice" role="status" style={{ marginTop: '0.5rem' }}>
              <span aria-hidden="true">{requireCustomerOtp ? '🔒' : '💤'}</span>
              <span>
                {requireCustomerOtp
                  ? t('admin.customerAuth.enabledNotice')
                  : t('admin.customerAuth.disabledNotice')}
              </span>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: '1rem' }}>
            {saving ? '…' : t('admin.settings.save')}
          </button>
        </form>
      </div>
    </div>
  );
}
